import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Trash2 } from 'lucide-react';

import { archiveProject, deleteProject, unarchiveProject } from '../../api/projects';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { useProject } from '../../hooks/useProject';
import { humanizeError } from '../../lib/errors';
import styles from './SettingsPages.module.css';

/** Опасная зона: архивация и безвозвратное удаление проекта (UC-PRJ-05). */
export function DangerSettingsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { project } = useProject(key);

  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(key) });
  };

  const archiveMutation = useMutation({
    mutationFn: () => (project?.isArchived ? unarchiveProject(key) : archiveProject(key)),
    onSuccess: () => {
      invalidate();
      toast.success(project?.isArchived ? 'Проект разархивирован' : 'Проект архивирован');
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (confirmKey: string) => deleteProject(key, confirmKey),
    onSuccess: () => {
      setDeleteOpen(false);
      invalidate();
      toast.success('Проект удалён');
      navigate('/');
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  return (
    <div className={styles['section']}>
      <div className={styles['dangerBlock']}>
        <h2>{project?.isArchived ? 'Разархивировать проект' : 'Архивировать проект'}</h2>
        <p className={styles['hint']}>
          {project?.isArchived
            ? 'Проект снова появится в списке активных и станет доступен для работы.'
            : 'Проект станет доступен только для чтения и скроется из списка активных. Данные сохранятся.'}
        </p>
        <div className={styles['row']}>
          <Button
            variant="secondary"
            icon={project?.isArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            loading={archiveMutation.isPending}
            onClick={() => archiveMutation.mutate()}
          >
            {project?.isArchived ? 'Разархивировать' : 'Архивировать'}
          </Button>
        </div>
      </div>

      <div className={styles['dangerBlock']}>
        <h2>Удалить проект</h2>
        <p className={styles['hint']}>
          Удаление необратимо: будут удалены все задачи, спринты, комментарии и вложения проекта.
        </p>
        <div className={styles['row']}>
          <Button variant="danger" icon={<Trash2 size={14} />} onClick={() => setDeleteOpen(true)}>
            Удалить проект
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={isDeleteOpen}
        title={`Удалить проект «${project?.name ?? key}»?`}
        description="Это действие нельзя отменить. Введите ключ проекта, чтобы подтвердить удаление."
        confirmLabel="Удалить навсегда"
        confirmPhrase={key}
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(key)}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
}
